import * as exConstants from './constants';

import { slideDown, slideUp } from '../effect';

// ----------------------------------------------------------------------------

type TypeConfig = {
    animationSpeed: number;
    collapseTrigger: string;
    removeTrigger: string;
    maximizeTrigger: string;
};

const DefaultConfig: TypeConfig = {
    animationSpeed: 500,
    collapseTrigger: exConstants.SELECTOR_DATA_COLLAPSE,
    removeTrigger: exConstants.SELECTOR_DATA_REMOVE,
    maximizeTrigger: exConstants.SELECTOR_DATA_MAXIMIZE,
};

// ----------------------------------------------------------------------------

class MyCardWidget {
    _element: HTMLElement;

    _parent: HTMLElement | undefined;

    _clone: HTMLElement | undefined;

    _config: TypeConfig;

    constructor(element: HTMLElement, config?: TypeConfig) {
        this._element = element;
        this._parent = element.closest(exConstants.SELECTOR_CARD) as HTMLElement | undefined;

        if (element.classList.contains(exConstants.CLASS_NAME_CARD)) {
            this._parent = element;
        }

        this._config = { ...DefaultConfig, ...config };
    }

    collapse(): void {
        const event = new Event(exConstants.EVENT_COLLAPSED);

        if (this._parent) {
            this._parent.classList.add(exConstants.CLASS_NAME_COLLAPSING);

            const elm = this._parent?.querySelectorAll(
                `${exConstants.SELECTOR_CARD_BODY}, ${exConstants.SELECTOR_CARD_FOOTER}`,
            );

            elm.forEach((el): any => {
                if (el instanceof HTMLElement) {
                    slideUp(el, this._config.animationSpeed);
                }
            });

            setTimeout((): any => {
                if (this._parent) {
                    this._parent.classList.add(exConstants.CLASS_NAME_COLLAPSED);
                    this._parent.classList.remove(exConstants.CLASS_NAME_COLLAPSING);
                }
            }, this._config.animationSpeed);
        }

        this._element?.dispatchEvent(event);
    }

    expand(): void {
        const event = new Event(exConstants.EVENT_EXPANDED);

        if (this._parent) {
            this._parent.classList.add(exConstants.CLASS_NAME_EXPANDING);

            const elm = this._parent?.querySelectorAll(
                `${exConstants.SELECTOR_CARD_BODY}, ${exConstants.SELECTOR_CARD_FOOTER}`,
            );

            elm.forEach((el): any => {
                if (el instanceof HTMLElement) {
                    slideDown(el, this._config.animationSpeed);
                }
            });

            setTimeout((): any => {
                if (this._parent) {
                    this._parent.classList.remove(exConstants.CLASS_NAME_COLLAPSED);
                    this._parent.classList.remove(exConstants.CLASS_NAME_EXPANDING);
                }
            }, this._config.animationSpeed);
        }

        this._element?.dispatchEvent(event);
    }

    remove(): void {
        const event = new Event(exConstants.EVENT_REMOVE);

        if (this._parent) {
            slideUp(this._parent, this._config.animationSpeed);
        }

        this._element?.dispatchEvent(event);
    }

    toggle(): void {
        if (this._parent?.classList.contains(exConstants.CLASS_NAME_COLLAPSED)) {
            this.expand();
            return;
        }

        this.collapse();
    }

    maximize(): void {
        const event = new Event(exConstants.EVENT_MAXIMIZED);

        if (this._parent) {
            this._parent.style.height = `${this._parent.offsetHeight}px`;
            this._parent.style.width = `${this._parent.offsetWidth}px`;
            this._parent.style.transition = 'all .15s';

            setTimeout((): any => {
                const htmlTag = document.querySelector('html');

                if (htmlTag) {
                    htmlTag.classList.add(exConstants.CLASS_NAME_MAXIMIZED);
                }

                if (this._parent) {
                    this._parent.classList.add(exConstants.CLASS_NAME_MAXIMIZED);

                    if (this._parent.classList.contains(exConstants.CLASS_NAME_COLLAPSED)) {
                        this._parent.classList.add(exConstants.CLASS_NAME_WAS_COLLAPSED);
                    }
                }
            }, 150);
        }

        this._element?.dispatchEvent(event);
    }

    minimize(): void {
        const event = new Event(exConstants.EVENT_MINIMIZED);

        if (this._parent) {
            this._parent.style.height = 'auto';
            this._parent.style.width = 'auto';
            this._parent.style.transition = 'all .15s';

            setTimeout((): any => {
                const htmlTag = document.querySelector('html');

                if (htmlTag) {
                    htmlTag.classList.remove(exConstants.CLASS_NAME_MAXIMIZED);
                }

                if (this._parent) {
                    this._parent.classList.remove(exConstants.CLASS_NAME_MAXIMIZED);

                    if (this._parent?.classList.contains(exConstants.CLASS_NAME_WAS_COLLAPSED)) {
                        this._parent.classList.remove(exConstants.CLASS_NAME_WAS_COLLAPSED);
                    }
                }
            }, 10);
        }

        this._element?.dispatchEvent(event);
    }

    toggleMaximize(): void {
        if (this._parent?.classList.contains(exConstants.CLASS_NAME_MAXIMIZED)) {
            this.minimize();
            return;
        }

        this.maximize();
    }
}

// ----------------------------------------------------------------------------

export default MyCardWidget;
